import { useState } from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import type { TagItem } from '../types';
import { TagCreateDialog } from './TagCreateDialog';

interface TagPickerWithCreateProps {
  tags: TagItem[];
  value: number | null;
  onChange: (tagId: number | null, tag: TagItem | null) => void;
  onCreated?: (tag: TagItem) => void;
  disabled?: boolean;
  allowClear?: boolean;
  placeholder?: string;
  clearLabel?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  createButtonLabel?: string;
  createDialogTitle?: string;
  createDialogDescription?: string;
  createSuccessMessage?: string;
  className?: string;
}

export function TagPickerWithCreate({
  tags,
  value,
  onChange,
  onCreated,
  disabled = false,
  allowClear = true,
  placeholder = 'Выбрать тег',
  clearLabel = 'Не выбрано',
  searchPlaceholder = 'Поиск тега...',
  emptyLabel = 'Теги не найдены',
  createButtonLabel = 'Создать новый тег',
  createDialogTitle,
  createDialogDescription,
  createSuccessMessage,
  className,
}: TagPickerWithCreateProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createdTags, setCreatedTags] = useState<TagItem[]>([]);

  const tagsById = new Map<number, TagItem>();
  for (const tag of [...createdTags, ...tags]) {
    tagsById.set(tag.id, tag);
  }

  const activeTags = [...tagsById.values()].filter((tag) => tag.deletedAt === null);
  const selectedTag = value === null ? null : activeTags.find((tag) => tag.id === value) ?? null;

  const handleCreated = (tag: TagItem) => {
    setCreatedTags((prev) => [tag, ...prev.filter((item) => item.id !== tag.id)]);
    onChange(tag.id, tag);
    onCreated?.(tag);
  };

  return (
    <>
      <div className={cn('flex gap-2', className)}>
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              type="button"
              disabled={disabled}
              className="min-w-0 flex-1 justify-between font-normal"
            >
              {selectedTag ? (
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className="h-3 w-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: selectedTag.color }}
                  />
                  <span className="truncate">{selectedTag.name}</span>
                </span>
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" sideOffset={4}>
            <Command>
              <CommandInput placeholder={searchPlaceholder} />
              <CommandList>
                <CommandEmpty>{emptyLabel}</CommandEmpty>
                <CommandGroup>
                  {allowClear && (
                    <CommandItem
                      value="__none__"
                      onSelect={() => {
                        onChange(null, null);
                        setPopoverOpen(false);
                      }}
                    >
                      <Check className={cn('mr-2 h-4 w-4', value === null ? 'opacity-100' : 'opacity-0')} />
                      {clearLabel}
                    </CommandItem>
                  )}
                  {activeTags.map((tag) => (
                    <CommandItem
                      key={tag.id}
                      value={`${tag.name}-${tag.id}`}
                      onSelect={() => {
                        onChange(tag.id, tag);
                        setPopoverOpen(false);
                      }}
                    >
                      <Check className={cn('mr-2 h-4 w-4', value === tag.id ? 'opacity-100' : 'opacity-0')} />
                      <span
                        className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={disabled}
          aria-label={createButtonLabel}
          onClick={() => {
            setPopoverOpen(false);
            setCreateDialogOpen(true);
          }}
          className="flex-shrink-0"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <TagCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handleCreated}
        title={createDialogTitle}
        description={createDialogDescription}
        successMessage={createSuccessMessage}
      />
    </>
  );
}

